<script setup>
import { ref, computed, watch } from 'vue';
import Spinner from 'shared/components/Spinner.vue';
import InstagramMediaReportsAPI from 'dashboard/api/instagramMediaReports';
import {
  formatDate,
  TYPE_LABELS,
  TYPE_COLORS,
} from './useMonitorHelpers';

const props = defineProps({
  selectedInbox: {
    type: Object,
    default: null,
  },
  fromDate: {
    type: Number,
    required: true,
  },
  toDate: {
    type: Number,
    required: true,
  },
  groupBy: {
    type: String,
    default: 'day',
  },
});

const isLoading = ref(false);
const reportData = ref(null);
const activeChart = ref('count');

const summary = computed(() => reportData.value?.summary || {});
const byDate = computed(() => reportData.value?.by_date || []);
const byType = computed(() => reportData.value?.by_type || []);
const topPosts = computed(() => reportData.value?.top_posts || []);

const maxCount = computed(() => {
  if (!byDate.value.length) return 0;
  return Math.max(...byDate.value.map(d => d[activeChart.value] || 0));
});

function getBarHeight(value) {
  if (!maxCount.value) return 0;
  return (value / maxCount.value) * 100;
}

function getBarColor() {
  if (activeChart.value === 'likes') return 'bg-n-pink-9';
  if (activeChart.value === 'comments') return 'bg-n-teal-9';
  return 'bg-n-blue-9';
}

function typeMaxCount() {
  if (!byType.value.length) return 1;
  return Math.max(1, ...byType.value.map(item => item.count));
}

function typeBarWidth(count) {
  return (count / typeMaxCount()) * 100;
}

async function fetchReport() {
  isLoading.value = true;
  try {
    const response = await InstagramMediaReportsAPI.getReport({
      from: props.fromDate,
      to: props.toDate,
      inboxId: props.selectedInbox?.id,
      groupBy: props.groupBy,
    });
    reportData.value = response.data;
  } catch {
    // silently fail
  } finally {
    isLoading.value = false;
  }
}

watch(
  () => [props.fromDate, props.toDate, props.selectedInbox, props.groupBy],
  () => fetchReport(),
  { immediate: true }
);
</script>

<template>
  <div>
    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>

    <template v-else-if="reportData">
      <!-- Summary Cards -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <p class="text-sm text-n-slate-11 mb-1">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.TOTAL_MEDIA') }}
          </p>
          <p class="text-2xl font-semibold text-n-slate-12">
            {{ summary.total || 0 }}
          </p>
        </div>
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <p class="text-sm text-n-slate-11 mb-1">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.TOTAL_REELS') }}
          </p>
          <p class="text-2xl font-semibold text-n-teal-11">
            {{ summary.reels || 0 }}
          </p>
        </div>
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <p class="text-sm text-n-slate-11 mb-1">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.TOTAL_LIKES') }}
          </p>
          <p class="text-2xl font-semibold text-n-pink-11">
            {{ summary.total_likes || 0 }}
          </p>
        </div>
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <p class="text-sm text-n-slate-11 mb-1">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.TOTAL_COMMENTS') }}
          </p>
          <p class="text-2xl font-semibold text-n-blue-11">
            {{ summary.total_comments || 0 }}
          </p>
        </div>
      </div>

      <!-- Second row: type breakdown cards -->
      <div class="grid grid-cols-3 md:grid-cols-5 gap-3 mb-6">
        <div
          class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center"
        >
          <p class="text-xs text-n-slate-11 mb-1">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.IMAGES') }}
          </p>
          <p class="text-lg font-semibold text-n-blue-11">
            {{ summary.images || 0 }}
          </p>
        </div>
        <div
          class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center"
        >
          <p class="text-xs text-n-slate-11 mb-1">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.VIDEOS') }}
          </p>
          <p class="text-lg font-semibold text-n-violet-11">
            {{ summary.videos || 0 }}
          </p>
        </div>
        <div
          class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center"
        >
          <p class="text-xs text-n-slate-11 mb-1">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.CAROUSELS') }}
          </p>
          <p class="text-lg font-semibold text-n-amber-11">
            {{ summary.carousels || 0 }}
          </p>
        </div>
        <div
          class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center"
        >
          <p class="text-xs text-n-slate-11 mb-1">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.REELS') }}
          </p>
          <p class="text-lg font-semibold text-n-teal-11">
            {{ summary.reels || 0 }}
          </p>
        </div>
        <div
          class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center"
        >
          <p class="text-xs text-n-slate-11 mb-1">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.STORIES') }}
          </p>
          <p class="text-lg font-semibold text-n-pink-11">
            {{ summary.stories || 0 }}
          </p>
        </div>
      </div>

      <!-- Timeline Chart -->
      <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-n-slate-12">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.TIMELINE_CHART') }}
          </h3>
          <div class="flex gap-2">
            <button
              class="px-3 py-1 text-xs rounded-lg border transition-colors"
              :class="
                activeChart === 'count'
                  ? 'bg-n-blue-9 text-white border-n-blue-9'
                  : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-n-slate-7'
              "
              @click="activeChart = 'count'"
            >
              {{ $t('INSTAGRAM_MEDIA_REPORTS.CHART_POSTS') }}
            </button>
            <button
              class="px-3 py-1 text-xs rounded-lg border transition-colors"
              :class="
                activeChart === 'likes'
                  ? 'bg-n-pink-9 text-white border-n-pink-9'
                  : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-n-slate-7'
              "
              @click="activeChart = 'likes'"
            >
              {{ $t('INSTAGRAM_MEDIA_REPORTS.CHART_LIKES') }}
            </button>
            <button
              class="px-3 py-1 text-xs rounded-lg border transition-colors"
              :class="
                activeChart === 'comments'
                  ? 'bg-n-teal-9 text-white border-n-teal-9'
                  : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-n-slate-7'
              "
              @click="activeChart = 'comments'"
            >
              {{ $t('INSTAGRAM_MEDIA_REPORTS.CHART_COMMENTS') }}
            </button>
          </div>
        </div>

        <div v-if="byDate.length" class="h-64">
          <div class="flex items-end justify-between h-full gap-1">
            <div
              v-for="item in byDate"
              :key="item.date"
              class="flex flex-col items-center flex-1"
            >
              <span class="text-xs text-n-slate-11 mb-1">
                {{ item[activeChart] || 0 }}
              </span>
              <div
                class="w-full rounded-t transition-all duration-300"
                :class="getBarColor()"
                :style="{
                  height: `${getBarHeight(item[activeChart] || 0)}%`,
                  minHeight: (item[activeChart] || 0) > 0 ? '4px' : '0',
                }"
              />
              <span
                class="text-xs text-n-slate-11 mt-2 rotate-45 origin-left whitespace-nowrap"
              >
                {{ formatDate(item.date) }}
              </span>
            </div>
          </div>
        </div>
        <div
          v-else
          class="flex items-center justify-center h-64 text-n-slate-11"
        >
          {{ $t('INSTAGRAM_MEDIA_REPORTS.NO_DATA') }}
        </div>
      </div>

      <!-- Type Breakdown Chart -->
      <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6">
        <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
          {{ $t('INSTAGRAM_MEDIA_REPORTS.TYPE_BREAKDOWN') }}
        </h3>
        <div v-if="byType.length" class="space-y-3">
          <div
            v-for="item in byType"
            :key="item.type"
            class="flex items-center gap-3"
          >
            <span class="text-sm font-medium w-24 text-n-slate-12">
              {{ TYPE_LABELS[item.type] || item.type }}
            </span>
            <div
              class="flex-1 h-8 bg-n-slate-3 rounded-lg overflow-hidden relative"
            >
              <div
                class="h-full rounded-lg transition-all duration-500"
                :class="TYPE_COLORS[item.type] || 'bg-n-blue-9'"
                :style="{
                  width: `${typeBarWidth(item.count)}%`,
                  minWidth: item.count > 0 ? '24px' : '0',
                }"
              />
              <span
                class="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-n-slate-12"
              >
                {{ item.count }}
              </span>
            </div>
            <div class="flex gap-3 text-xs text-n-slate-11 w-40">
              <span>{{
                $t('INSTAGRAM_MEDIA_REPORTS.LIKES_LABEL', {
                  count: item.likes,
                })
              }}</span>
              <span>{{
                $t('INSTAGRAM_MEDIA_REPORTS.COMMENTS_LABEL', {
                  count: item.comments,
                })
              }}</span>
            </div>
          </div>
        </div>
        <div
          v-else
          class="flex items-center justify-center h-32 text-n-slate-11"
        >
          {{ $t('INSTAGRAM_MEDIA_REPORTS.NO_DATA') }}
        </div>
      </div>

      <!-- Top Posts -->
      <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6">
        <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
          {{ $t('INSTAGRAM_MEDIA_REPORTS.TOP_POSTS') }}
        </h3>
        <div v-if="topPosts.length" class="space-y-3">
          <div
            v-for="(post, idx) in topPosts"
            :key="post.id"
            class="flex items-center gap-4 p-3 rounded-lg border border-n-weak hover:bg-n-alpha-1 transition-colors"
          >
            <span class="text-lg font-bold text-n-slate-9 w-8 text-center">
              {{ idx + 1 }}
            </span>
            <div
              v-if="post.thumbnail_url"
              class="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-n-slate-3"
            >
              <img
                :src="post.thumbnail_url"
                class="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm text-n-slate-12 truncate">
                {{ post.caption || $t('INSTAGRAM_MEDIA_REPORTS.NO_CAPTION') }}
              </p>
              <div class="flex gap-3 mt-1">
                <span
                  class="text-xs px-2 py-0.5 rounded-full text-white"
                  :class="[TYPE_COLORS[post.media_type] || 'bg-n-blue-3']"
                >
                  {{ TYPE_LABELS[post.media_type] || post.media_type }}
                </span>
                <span class="text-xs text-n-slate-11">
                  {{ post.timestamp ? formatDate(post.timestamp) : '' }}
                </span>
              </div>
            </div>
            <div class="flex gap-4 text-sm flex-shrink-0">
              <span class="text-n-pink-11 font-medium">
                {{
                  $t('INSTAGRAM_MEDIA_REPORTS.LIKES_LABEL', {
                    count: post.like_count,
                  })
                }}
              </span>
              <span class="text-n-blue-11 font-medium">
                {{
                  $t('INSTAGRAM_MEDIA_REPORTS.COMMENTS_LABEL', {
                    count: post.comments_count,
                  })
                }}
              </span>
            </div>
            <a
              v-if="post.permalink"
              :href="post.permalink"
              target="_blank"
              rel="noopener noreferrer"
              class="text-n-blue-11 hover:text-n-blue-12 text-sm flex-shrink-0"
            >
              {{ $t('INSTAGRAM_MEDIA_REPORTS.OPEN_LINK') }}
            </a>
          </div>
        </div>
        <div
          v-else
          class="flex items-center justify-center h-32 text-n-slate-11"
        >
          {{ $t('INSTAGRAM_MEDIA_REPORTS.NO_DATA') }}
        </div>
      </div>
    </template>
  </div>
</template>
